import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { BaElsewhereNote } from '@platform/engine/insights/BaElsewhereNote';
import { PageFrame } from '@platform/engine/list/header/PageFrame';
import { PageHeader } from '@platform/engine/list/header/PageHeader';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { insightPageOf } from '@platform/engine/menu/modulePages';
import { AiAdvice } from './AiAdvice';
import { ExpertAdvice } from './ExpertAdvice';
import type { IModulePageProps } from './IModulePageProps';
import { useModuleAdvice } from './useModuleAdvice';
import { usePresetLink } from './usePresetLink';
import './modulePages.css';

/** Сторінка матриці ризиків модуля (аналітичне подання): дія поради про відкриті критичні ризики (прототип v4: views.rsk = riskmatrix). */
const RISK_MATRIX = 'riskmatrix';

/**
 * Поради (прототип v4 isAdvisory): крихти, назва модуля над «Поради», «Поради ШІ та експертів для цього модуля.»; дві панелі поруч —
 * «Поради ШІ» (поради за станом записів із діями, для ризиків — і «Матриця ризиків») і «Поради експертів» (експертні висновки й статті бази знань).
 */
export function ModuleAdvisoryPage({ ws, module, rt }: IModulePageProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings(module.module.key);
  const { stats, advice } = useModuleAdvice(ws, rt);
  const linkOf = usePresetLink(module, rt, strings, stats?.buckets);
  const moduleName = strings.moduleName(module.module.name);
  const crumbs = useModuleCrumbs(module.module.key, moduleName);
  const stageLabel = (value: string) => strings.status(rt.key, value, rt.statuses.list.find((s) => s.value === value)?.label);
  const matrixTo = insightPageOf(module, RISK_MATRIX) ? routes.modulePage(module.module.key, RISK_MATRIX) : undefined;
  return (
    <PageFrame crumbs={crumbs} head={<PageHeader eyebrow={moduleName} title={t('modulePages.advisory')} description={t('advisory.intro')} />}>
      <BaElsewhereNote ws={ws} moduleKey={module.module.key} rtKey={rt.key} />
      <div className="mp-grid">
        <AiAdvice items={advice} linkOf={linkOf} matrixTo={matrixTo} stageLabel={stageLabel} />
        <ExpertAdvice ws={ws} />
      </div>
    </PageFrame>
  );
}
